import { ChamberMemberProEmailBodyFormatEnum, CmpInternalEmailKeyEnum } from "../../../../../graphql/types";
import { EmailTemplate } from "../settingsData";

/**
 * Monday 13225040388 (N4b) and 13362632215: the design (25) "Internal Emails
 * Sent from Chamber Member Pro™" section (CMP Dashboard.dc.html:9473, data
 * :17161). Each card has an ENABLED/DISABLED switch and an editable Send To
 * list; the backend (cmpInternalEmailSettings.ts) owns who receives each email.
 *
 * The subjects and bodies follow the design (Moeen, Nov 2 2026) and match what
 * the backend sends:
 * - eventRegistration: cmpEventEmails.ts renderEventStaffNotice
 * - prospectLead: the chamber's saved "1. New Prospect Lead Received" template
 * - memberApplication: cmpMemberEmails.ts sendCmpChamberApplicationReceivedEmailSafely
 * - invoiceCreated / invoicePaid: cmpChamberInvoiceCopies.ts renderCmpChamberInvoiceCopy
 * Change both places together.
 */
export const INTERNAL_EMAIL_SENDER = "info@chambermemberpro.com";
export const PROSPECT_LEAD_TEMPLATE_ID = "prospect-lead-received";

export type InternalEmailKey = "eventRegistration" | "prospectLead" | "memberApplication" | "invoiceCreated" | "invoicePaid";

export type InternalEmail = {
  key: InternalEmailKey;
  title: string;
  description: string;
  subject: string;
  /** Plain text; a blank line starts a new paragraph and **text** is bold. */
  body: string;
  /** The backend card (getCmpInternalEmailSettings / updateCmpInternalEmailSetting). */
  apiKey: CmpInternalEmailKeyEnum;
  /** Text under the subject and in the pop-up when the content can be edited elsewhere. */
  editNote?: string;
};

export const internalEmails: InternalEmail[] = [
  {
    key: "eventRegistration",
    apiKey: CmpInternalEmailKeyEnum.EventRegistration,
    title: "Event Registration Confirmation",
    description: "Sent to your chamber each time a ticket or sponsorship registration is completed, with the event, attendee, company and ticket details.",
    subject: "New Event Registration: [Event Name] – [Company Name]",
    body: [
      "Hi [Chamber Name],",
      "",
      "A new registration has been received for **[Event Name]**. Please find the registration details below.",
      "",
      "**Event Registration Details**",
      "",
      "**Event:** [Event Name]",
      "**Date:** [Event Date]",
      "**Company:** [Company Name]",
      "**Attendee:** [Attendee Name]",
      "**Ticket / Sponsorship:** [Ticket Type]",
      "**Quantity:** [Quantity]",
      "**Invoice Type:** [Invoice Type]",
      "**Invoice #:** [Invoice Number]",
      "",
      "To view the complete registration details, go to **Manage Tickets** in your Chamber Member Pro™ Dashboard.",
      "",
      "Regards,",
      "**Chamber Member Pro™**",
    ].join("\n"),
  },
  {
    key: "prospectLead",
    apiKey: CmpInternalEmailKeyEnum.ProspectLead,
    title: "New Prospect Lead",
    description: "Sent to your chamber when a company sends a membership inquiry from the Membership Options page of your chamber website, with the company and contact details.",
    subject: "",
    body: "",
    editNote: "Edit this email in 1. New Prospect Lead Received below.",
  },
  {
    key: "memberApplication",
    apiKey: CmpInternalEmailKeyEnum.MemberApplication,
    title: "New Member Application",
    description: "Sent to your chamber when a company submits a new membership application, so it can be reviewed and approved.",
    subject: "New Membership Application: [Company Name]",
    body: [
      "Hi [Chamber Name],",
      "",
      "A new membership application has been submitted by **[Company Name]**. Please find the application details below.",
      "",
      "**Membership Application Details**",
      "",
      "**Company:** [Company Name]",
      "**Primary Contact:** [Primary Contact Name]",
      "**Membership Type:** [Membership Type]",
      "**Submitted:** [Submitted Date]",
      "",
      "To review and approve the application, go to **Manage Members** in your Chamber Member Pro™ Dashboard.",
      "",
      "Regards,",
      "**Chamber Member Pro™**",
    ].join("\n"),
  },
  {
    key: "invoiceCreated",
    apiKey: CmpInternalEmailKeyEnum.InvoiceCreated,
    title: "Sent Invoices",
    description: "Sent to your chamber as a copy each time an invoice is sent to a member or non-member, with the invoice type, invoice number, amount and due date.",
    subject: "Invoice Sent: [Invoice Number] – [Company Name]",
    body: [
      "Hi [Chamber Name],",
      "",
      "An invoice has been sent to **[Company Name]**. Please find the invoice details below.",
      "",
      "**Invoice Details**",
      "",
      "**Invoice Type:** [Invoice Type]",
      "**Invoice #:** [Invoice Number]",
      "**Company:** [Company Name]",
      "**Amount:** [Invoice Amount]",
      "**Due Date:** [Due Date]",
      "",
      "To view the complete invoice details, go to **Manage All Invoices & Payments** in your Chamber Member Pro™ Dashboard.",
      "",
      "Regards,",
      "**Chamber Member Pro™**",
    ].join("\n"),
  },
  {
    key: "invoicePaid",
    apiKey: CmpInternalEmailKeyEnum.InvoicePaid,
    title: "Paid Invoice Receipts",
    description: "Sent to your chamber when an invoice is paid, with the payment receipt, what the payment is for (invoice type), invoice number, amount, payment method and date paid.",
    subject: "Payment Received: [Invoice Number] – [Company Name]",
    body: [
      "Hi [Chamber Name],",
      "",
      "A payment has been received from **[Company Name]**. Please find the payment details below.",
      "",
      "**Payment Details**",
      "",
      "**Receipt For:** [Invoice Type]",
      "**Invoice #:** [Invoice Number]",
      "**Company:** [Company Name]",
      "**Amount Paid:** [Amount Paid]",
      "**Payment Method:** [Payment Method]",
      "**Date Paid:** [Date Paid]",
      "",
      "To view the payment receipt, go to **Manage All Invoices & Payments** in your Chamber Member Pro™ Dashboard.",
      "",
      "Regards,",
      "**Chamber Member Pro™**",
    ].join("\n"),
  },
];

// cmpMemberEmails.ts CMP_PROSPECT_LEAD_SOURCE_HINT: the send removes this
// design hint because [Lead Source] already names the source.
const PROSPECT_LEAD_SOURCE_HINT = " (Membership Options page inquiry or email subscriber sign-up)";

/** Saved template HTML as the plain text the view pop-up shows, bold kept as **text**. */
export const getInternalEmailTemplateText = (html: string) => {
  const marked = html.split(PROSPECT_LEAD_SOURCE_HINT).join("")
    .replace(/<\s*\/?\s*(strong|b)(\s[^>]*)?>/gi, "**")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n\n");
  const text = new DOMParser().parseFromString(marked, "text/html").body.textContent || "";
  return text.replace(/\u00A0/g, " ").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
};

/** The saved New Prospect Lead template as the pop-up shows it, or null when it is missing. */
export const getProspectLeadEmailView = (template?: Pick<EmailTemplate, "subject" | "body" | "bodyFormat"> | null) => template ? {
  subject: template.subject,
  body: template.bodyFormat === ChamberMemberProEmailBodyFormatEnum.Html
    ? getInternalEmailTemplateText(template.body)
    : template.body.split(PROSPECT_LEAD_SOURCE_HINT).join("").trim(),
} : null;
